import * as Types from 'DVWebNodeWpsRuntime';
import { APIClient, AccessToken } from "./APIClient";
import { InitialArgs } from "../types/request-context";
import { Cache, getKeyUnrecognizedCustomer } from '../utils/cache'
import { Logger } from "../logger/logger";
import { operationToCamelCase } from './util';
import {
    DeregisterDeviceInput,
    DeregisterDeviceOutput,
    FeedbackGetQuestionInput,
    FeedbackSubmitAnswerInput,
    FeedbackProxyOutput,
    FtueCollection,
    FtueCollectionInput,
    SetAutoplaySettingsInput,
    SetAutoplaySettingsOutput,
    SetSubtitlesSettingsInput,
    SetSubtitlesSettingsOutput,
    GetDetailPageInput,
    GetPreferencesInput,
    GetStringsInput,
    GetTitlesInput,
    Inspector,
    LibraryInput,
    LibraryOutput,
    MyStuffInput,
    MyStuffOutput,
    StorefrontEnrichItemsInput,
    StorefrontEnrichItemsOutput,
    WatchlistInput,
    WatchlistOutput,
    WPS,
    WPSConfig,
    EPGInput,
    EPGOutput,
    HelpInput,
    HelpOutput,
    HelpSearchInput,
    HelpSearchOutput,
    WatchlistToggleInput,
    GetPaymentStateMessagesInput,
    GetPaymentStateMessagesOutput,
    CodeBasedLinkingInput,
    CodeBasedLinkingOutput,
    StorefrontOutput,
    StorefrontInput,
    RecordDialogDismissalInput,
    RecordDialogDismissalOutput,
    SettingsInput,
    SettingsOutput,
    GetVtrSelfServiceDataInput,
    GetVtrSelfServiceDataOutput,
    UpdateVtrInput,
    UpdateVtrOutput,
    GetRoadblockPageDataInput,
    GetRoadblockPageDataOutput,
    SetPinOrUsageInput,
    SetPinOrUsageOutput,
    SetViewingRestrictionsInput,
    SetViewingRestrictionsOutput,
    CreateProfileInput,
    CreateProfileOutput,
    GetProfilesInput,
    GetProfilesOutput,
    ProfileViewInput,
    ProfileViewOutput,
    EditProfileInput,
    EditProfileOutput,
    DeleteProfileInput,
    DeleteProfileOutput,
    SwitchProfileInput,
    SwitchProfileOutput,
    GetOptOutEmailsInput,
    GetOptOutEmailsOutput,
    SetOptOutEmailsInput,
    SetOptOutEmailsOutput,
    WatchHistoryStateChangeInput,
    WatchHistoryStateChangeOutput,
    VideoWizardInput,
    VideoWizardOutput,
    SetPreferencesInput,
    GetPangaeaBannerInput,
    GetGiftCardInput,
    RedeemCodeInput,
    SubmitVideoWizardInput,
    SubmitVideoWizardOutput,
    EnrichItemMetadataInput,
    EnrichItemMetadataOutput,
    Metadata,
    MetadataList,
    GetPersonDetailsInput,
    ScoutInput,
    ScoutOutput,
    ContactUsInput,
    ContactUsOutput,
    ContactUsChatInput,
    ContactUsChatOutput
} from "../types/wps";

import { Services } from "./services";
import { constructRequest, OperationType } from './request';

// Cache duration 120 seconds and maximum keys stored 100
const navFooterMemoryCache = new Cache({ ttl: 120, limit: 100 });

export enum Operation {
    Config = 'Config',
    ContactUs = 'ContactUs',
    ContactUsChat = 'ContactUsChat',
    DeregisterDevice = 'DeregisterDevice',
    FirstTimeUserExperience = 'FirstTimeUserExperience',
    Footer = 'Footer',
    SetAutoplaySettings = 'SetAutoplaySettings',
    SetSubtitlesSettings = 'SetSubtitlesSettings',
    GetDetailPage = 'GetDetailPage',
    GetPangaeaBanner = 'GetPangaeaBanner',
    GetGiftCard = 'GetGiftCard',
    RedeemCode = 'RedeemCode',
    GetPreferences = 'GetPreferences',
    SetPreferences = 'SetPreferences',
    GetRelatedTitles = 'GetRelatedTitles',
    GetStrings = 'GetStrings',
    GetTitles = 'GetTitles',
    Inspector = 'Inspector',
    Library = 'Library',
    MyStuffWatchlist = 'MyStuffWatchlist',
    MyStuffLibrary = 'MyStuffLibrary',
    Navigation = 'Navigation',
    Search = 'Search',
    SearchSuggestions = 'SearchSuggestions',
    StorefrontEnrichItems = 'StorefrontEnrichItems',
    Watchlist = 'Watchlist',
    EPG = 'EPG',
    FeedbackGetQuestion = 'FeedbackGetQuestion',
    FeedbackSubmitAnswer = 'FeedbackSubmitAnswer',
    Help = 'Help',
    HelpSearch = 'HelpSearch',
    WatchlistToggle = 'WatchlistToggle',
    GetPaymentStateMessages = 'GetPaymentStateMessages',
    GetPaymentSettings = 'GetPaymentSettings',
    CodeBasedLinking = 'CodeBasedLinking',
    Storefront = 'Storefront',
    RecordDialogDismissal = 'RecordDialogDismissal',
    GetChannelsSettingsPage = 'GetChannelsSettingsPage',
    GetLanguageSettingsPage = 'GetLanguageSettingsPage',
    GetPlaybackSettingsPage = 'GetPlaybackSettingsPage',
    GetPaymentHistorySettingsPage = 'GetPaymentHistorySettingsPage',
    GetSubtitlesSettingsPage = 'GetSubtitlesSettingsPage',
    GetWatchHistorySettingsPage = 'GetWatchHistorySettingsPage',
    GetYourAccountSettingsPage = 'GetYourAccountSettingsPage',
    GetYourDevicesSettingsPage = 'GetYourDevicesSettingsPage',
    GetVtrSelfServiceData = 'GetVtrSelfServiceData',
    UpdateVtr = 'UpdateVtr',
    GetRoadblockPageData = 'GetRoadblockPageData',
    GetParentalControlsSettingsPage = 'GetParentalControlsSettingsPage',
    SetPinOrUsage = 'SetPinOrUsage',
    SetViewingRestrictions = 'SetViewingRestrictions',
    CreateProfile = 'CreateProfile',
    GetProfiles = 'GetProfiles',
    ProfileView = 'ProfileView',
    EditProfile = 'EditProfile',
    DeleteProfile = 'DeleteProfile',
    SwitchProfile = 'SwitchProfile',
    GetOptOutEmails = 'GetOptOutEmails',
    SetOptOutEmails = 'SetOptOutEmails',
    WatchHistoryStateChange = 'WatchHistoryStateChange',
    VideoWizard = 'VideoWizard',
    SubmitVideoWizard = 'SubmitVideoWizard',
    EnrichItemMetadata = 'EnrichItemMetadata',
    MetadataList = 'MetadataList',
    Metadata = 'Metadata',
    GetPersonDetails = 'GetPersonDetails',
    Scout = 'Scout'
}

export enum AuthMode {
    ON,
    OFF,
    SHADOW,
}

export class WPSClient {
    api: APIClient;
    logger: Logger;
    endpoint?: string;
    constructor(api = new APIClient()) {
        this.api = api;
        this.logger = api.logger;
        this.endpoint = api.endpoints[Services.ATVWebPresentationService];
    }

    call = async <Req extends InitialArgs, Res>(operation: Operation, body: Req, operationType: OperationType, httpTimeout?: number) => {
        if (global.__SERVER__) {
            let headers: Record<string, string> = {
                'x-amz-target': `atv.wps.ATVWebPresentationService.${operation}`,
            };

            let accessToken: AccessToken;
            if (this.api.authentication && this.api.authentication.authMode !== AuthMode.OFF) {
                try {
                    if (!this.endpoint) {
                        throw new Error('Endpoint is invalid');
                    }

                    const { URL } = require('url');
                    const url: URL = new URL(this.endpoint);
                    if (url.protocol !== 'https:') {
                        throw new Error(`Given end point protocol '${url.protocol}' is incompatible with AuthMode '${this.api.authentication.authMode}'. Only https is supported.`);
                    }

                    const hostname: string = url.hostname;
                    accessToken = await this.api.authentication.cloudauthClient.getAccessToken({
                        host: hostname,
                        realm: 'ATVWebPresentationService',
                        scope: `https://aaa.amazon.com/scopes/ATVWebPresentationService#${operation}`,
                    });
                    headers.Authorization = `${accessToken.tokenType} ${accessToken.token}`;
                } catch (err) {
                    this.logger.error('Error occurred while generating access token', err);
                    // We shouldn't throw in SHADOW mode
                    if (this.api.authentication.authMode === AuthMode.ON) {
                        throw err;
                    }
                }
            }

            const req = constructRequest(this.logger, body.context.requestID, this.endpoint, body, {
                name: `${Services.ATVWebPresentationService} ${operation}`,
                requester: 'WPSClient',
                operationType,
                headers,
                timeout: httpTimeout,
                agent: this.api.agent
            });

            return await this.api.call(...req) as Res;
        } else {
            const url = `/gp/video/api/${operationToCamelCase(operation)}`;
            const req = constructRequest(this.logger, body.context.requestID, url, body, {
                name: `${Services.ATVWebPresentationService} ${operation}`,
                requester: 'WPSClient',
                operationType,
                timeout: httpTimeout
            });
            return await this.api.call(...req) as Res;
        }
    }

    async Footer(body: WPS.Footer.Request, enableCache = false) {
        let key;
        if (enableCache) {
            // The Cache key is determined based on the locale, the domain and the region of the request context
            key = `footer-${getKeyUnrecognizedCustomer(body.context)}`
            const cacheData = navFooterMemoryCache.get(key)
            if (cacheData) {
                this.logger.count('DVWebNodeCacheFooter', 1, false)
                return cacheData
            }
            const response = await this.call<WPS.Footer.Request, WPS.Footer.Response>(Operation.Footer, body, OperationType.READ);
            navFooterMemoryCache.put(key, response);
            return response;
        } else {
            return await this.call<WPS.Footer.Request, WPS.Footer.Response>(Operation.Footer, body, OperationType.READ);
        }
    }

    async Navigation(body: WPS.Navigation.Request, enableCache = false) {
        let key;
        if (enableCache && !body.context.customerID) {
            // The Cache key is determined based on the locale, the domain and the region of the request context only for unRecognizedCustomers
            key = `nav-${getKeyUnrecognizedCustomer(body.context, ['recordTerritory'])}-${body.pageGating}-${body.searchPhrase}-${body.isElcano}`
            const cacheData = navFooterMemoryCache.get(key)
            if (cacheData) {
                this.logger.count('DVWebNodeCacheNavigation', 1, false)
                return cacheData
            }
            const response = await this.call<WPS.Navigation.Request, WPS.Navigation.Response>(Operation.Navigation, body, OperationType.READ)
            navFooterMemoryCache.put(key, response)
            return response
        } else {
            return await this.call<WPS.Navigation.Request, WPS.Navigation.Response>(Operation.Navigation, body, OperationType.READ)
        }
    }

    async ContactUs(body: ContactUsInput) {
        return await this.call<ContactUsInput, ContactUsOutput>(Operation.ContactUs, body, OperationType.READ);
    }

    async ContactUsChat(body: ContactUsChatInput) {
        return await this.call<ContactUsChatInput, ContactUsChatOutput>(Operation.ContactUsChat, body, OperationType.READ);
    }

    async Help(body: HelpInput) {
        return await this.call<HelpInput, HelpOutput>(Operation.Help, body, OperationType.READ);
    }

    async HelpSearch(body: HelpSearchInput) {
        return await this.call<HelpSearchInput, HelpSearchOutput>(Operation.HelpSearch, body, OperationType.READ);
    }

    async Search(body: WPS.Search.Request) {
        return await this.call<WPS.Search.Request, WPS.Search.Response>(Operation.Search, body, OperationType.READ);
    }

    async SearchSuggestions(body: WPS.SearchSuggestions.Request) {
        return await this.call<WPS.SearchSuggestions.Request, WPS.SearchSuggestions.Response>(Operation.SearchSuggestions, body, OperationType.READ);
    }

    async Watchlist(body: WatchlistInput) {
        return await this.call<WatchlistInput, WatchlistOutput>(Operation.Watchlist, body, OperationType.READ);
    }

    async WatchlistToggle(body: WatchlistToggleInput) {
        return await this.call<WatchlistToggleInput, Types.atv.wps.WatchlistToggleOutputProps>(Operation.WatchlistToggle, body, OperationType.WRITE);
    }

    async Inspector(body: Inspector.Request) {
        return await this.call<Inspector.Request, Inspector.Response>(Operation.Inspector, body, OperationType.READ);
    }

    async Library(body: LibraryInput) {
        return await this.call<LibraryInput, LibraryOutput>(Operation.Library, body, OperationType.READ);
    }

    async MyStuffWatchlist(body: MyStuffInput) {
        return await this.call<MyStuffInput, MyStuffOutput>(Operation.MyStuffWatchlist, body, OperationType.READ);
    }
    async MyStuffLibrary(body: MyStuffInput) {
        return await this.call<MyStuffInput, MyStuffOutput>(Operation.MyStuffLibrary, body, OperationType.READ);
    }

    async GetOptOutEmails(body: GetOptOutEmailsInput) {
        return await this.call<GetOptOutEmailsInput, GetOptOutEmailsOutput>(Operation.GetOptOutEmails, body, OperationType.READ);
    }

    async SetOptOutEmails(body: SetOptOutEmailsInput) {
        return await this.call<SetOptOutEmailsInput, SetOptOutEmailsOutput>(Operation.SetOptOutEmails, body, OperationType.WRITE);
    }

    async StorefrontEnrichItems(body: StorefrontEnrichItemsInput) {
        return await this.call<StorefrontEnrichItemsInput, StorefrontEnrichItemsOutput>(Operation.StorefrontEnrichItems, body, OperationType.READ);
    }

    async GetTitles(body: GetTitlesInput) {
        return await this.call<GetTitlesInput, Types.atv.wps.GetTitlesOutputProps>(Operation.GetTitles, body, OperationType.READ);
    }

    async GetStrings(body: GetStringsInput) {
        return await this.call<GetStringsInput, Types.atv.wps.GetStringsOutputProps>(Operation.GetStrings, body, OperationType.READ);
    }

    async GetDetailPage(body: GetDetailPageInput) {
        return await this.call<GetDetailPageInput, Types.atv.wps.GetDetailPageOutputProps>(Operation.GetDetailPage, body, OperationType.READ);
    }

    async GetPangaeaBanner(body: GetPangaeaBannerInput) {
        return await this.call<GetPangaeaBannerInput, Types.atv.wps.GetPangaeaBannerOutputProps>(Operation.GetPangaeaBanner, body, OperationType.READ);
    }

    GetGiftCard = async (body: GetGiftCardInput) => {
        return await this.call<GetGiftCardInput, Types.atv.wps.GetGiftCardOutputProps>(Operation.GetGiftCard, body, OperationType.READ);
    }

    RedeemCode = async (body: RedeemCodeInput) => {
        return await this.call<RedeemCodeInput, Types.atv.wps.RedeemCodeOutputProps>(Operation.RedeemCode, body, OperationType.WRITE);
    }

    async DeregisterDevice(body: DeregisterDeviceInput) {
        return await this.call<DeregisterDeviceInput, DeregisterDeviceOutput>(Operation.DeregisterDevice, body, OperationType.WRITE);
    }

    async SetAutoplaySettings(body: SetAutoplaySettingsInput) {
        return await this.call<SetAutoplaySettingsInput, SetAutoplaySettingsOutput>(Operation.SetAutoplaySettings, body, OperationType.WRITE);
    }

    async SetSubtitlesSettings(body: SetSubtitlesSettingsInput) {
        return await this.call<SetSubtitlesSettingsInput, SetSubtitlesSettingsOutput>(Operation.SetSubtitlesSettings, body, OperationType.WRITE);
    }

    async FeedbackGetQuestion(body: FeedbackGetQuestionInput) {
        return await this.call<FeedbackGetQuestionInput, FeedbackProxyOutput>(Operation.FeedbackGetQuestion, body, OperationType.READ);
    }

    async FeedbackSubmitAnswer(body: FeedbackSubmitAnswerInput) {
        return await this.call<FeedbackSubmitAnswerInput, FeedbackProxyOutput>(Operation.FeedbackSubmitAnswer, body, OperationType.WRITE);
    }

    async FirstTimeUserExperience(body: FtueCollectionInput) {
        return await this.call<FtueCollectionInput, FtueCollection>(Operation.FirstTimeUserExperience, body, OperationType.READ);
    }

    async GetPreferences(body: GetPreferencesInput) {
        return await this.call<GetPreferencesInput, Types.atv.wps.GetPreferencesOutputProps>(Operation.GetPreferences, body, OperationType.READ);
    }

    async SetPreferences(body: SetPreferencesInput) {
        return await this.call<SetPreferencesInput, Types.atv.wps.SetPreferencesOutputProps>(Operation.SetPreferences, body, OperationType.WRITE);
    }

    async Config(body: WPSConfig.Request) {
        return await this.call<WPSConfig.Request, WPSConfig.Response>(Operation.Config, body, OperationType.READ);
    }

    async EPG(body: EPGInput) {
        return await this.call<EPGInput, EPGOutput>(Operation.EPG, body, OperationType.READ);
    }

    async GetPaymentStateMessages(body: GetPaymentStateMessagesInput) {
        return await this.call<GetPaymentStateMessagesInput, GetPaymentStateMessagesOutput>(Operation.GetPaymentStateMessages, body, OperationType.READ);
    }

    async CodeBasedLinking(body: CodeBasedLinkingInput) {
        return await this.call<CodeBasedLinkingInput, CodeBasedLinkingOutput>(Operation.CodeBasedLinking, body, OperationType.WRITE);
    }

    async Storefront(body: StorefrontInput) {
        return await this.call<StorefrontInput, StorefrontOutput>(Operation.Storefront, body, OperationType.READ);
    }

    async RecordDialogDismissal(body: RecordDialogDismissalInput) {
        return await this.call<RecordDialogDismissalInput, RecordDialogDismissalOutput>(Operation.RecordDialogDismissal, body, OperationType.WRITE);
    }

    async GetChannelsSettingsPage(body: SettingsInput) {
        return await this.call<SettingsInput, SettingsOutput>(Operation.GetChannelsSettingsPage, body, OperationType.READ);
    }

    async GetLanguageSettingsPage(body: SettingsInput) {
        return await this.call<SettingsInput, SettingsOutput>(Operation.GetLanguageSettingsPage, body, OperationType.READ);
    }

    async GetPlaybackSettingsPage(body: SettingsInput) {
        return await this.call<SettingsInput, SettingsOutput>(Operation.GetPlaybackSettingsPage, body, OperationType.READ);
    }

    async GetPaymentHistorySettingsPage(body: SettingsInput) {
        return await this.call<SettingsInput, SettingsOutput>(Operation.GetPaymentHistorySettingsPage, body, OperationType.READ);
    }
    async GetSubtitlesSettingsPage(body: SettingsInput) {
        return await this.call<SettingsInput, SettingsOutput>(Operation.GetSubtitlesSettingsPage, body, OperationType.READ);
    }

    async GetWatchHistorySettingsPage(body: SettingsInput) {
        return await this.call<SettingsInput, SettingsOutput>(Operation.GetWatchHistorySettingsPage, body, OperationType.READ);
    }

    async GetYourAccountSettingsPage(body: SettingsInput) {
        return await this.call<SettingsInput, SettingsOutput>(Operation.GetYourAccountSettingsPage, body, OperationType.READ);
    }

    async GetYourDevicesSettingsPage(body: SettingsInput) {
        return await this.call<SettingsInput, SettingsOutput>(Operation.GetYourDevicesSettingsPage, body, OperationType.READ);
    }

    async GetVtrSelfServiceData(body: GetVtrSelfServiceDataInput) {
        return await this.call<GetVtrSelfServiceDataInput, GetVtrSelfServiceDataOutput>(Operation.GetVtrSelfServiceData, body, OperationType.READ);
    }

    async UpdateVtr(body: UpdateVtrInput) {
        return await this.call<UpdateVtrInput, UpdateVtrOutput>(Operation.UpdateVtr, body, OperationType.WRITE);
    }

    async GetRoadbloackPageData(body: GetRoadblockPageDataInput) {
        return await this.call<GetRoadblockPageDataInput, GetRoadblockPageDataOutput>(Operation.GetRoadblockPageData, body, OperationType.READ);
    }

    async GetParentalControlsSettingsPage(body: SettingsInput) {
        return await this.call<SettingsInput, SettingsOutput>(Operation.GetParentalControlsSettingsPage, body, OperationType.READ);
    }

    async SetPinOrUsage(body: SetPinOrUsageInput) {
        return await this.call<SetPinOrUsageInput, SetPinOrUsageOutput>(Operation.SetPinOrUsage, body, OperationType.WRITE);
    }

    async SetViewingRestrictions(body: SetViewingRestrictionsInput) {
        return await this.call<SetViewingRestrictionsInput, SetViewingRestrictionsOutput>(Operation.SetViewingRestrictions, body, OperationType.WRITE);
    }

    async CreateProfile(body: CreateProfileInput) {
        return await this.call<CreateProfileInput, CreateProfileOutput>(Operation.CreateProfile, body, OperationType.WRITE);
    }

    async GetProfiles(body: GetProfilesInput) {
        return await this.call<GetProfilesInput, GetProfilesOutput>(Operation.GetProfiles, body, OperationType.READ);
    }

    async ProfileView(body: ProfileViewInput) {
        return await this.call<ProfileViewInput, ProfileViewOutput>(Operation.ProfileView, body, OperationType.READ);
    }

    async EditProfile(body: EditProfileInput) {
        return await this.call<EditProfileInput, EditProfileOutput>(Operation.EditProfile, body, OperationType.WRITE);
    }

    async DeleteProfile(body: DeleteProfileInput) {
        return await this.call<DeleteProfileInput, DeleteProfileOutput>(Operation.DeleteProfile, body, OperationType.WRITE);
    }

    async SwitchProfile(body: SwitchProfileInput) {
        return await this.call<SwitchProfileInput, SwitchProfileOutput>(Operation.SwitchProfile, body, OperationType.WRITE);
    }

    async WatchHistoryStateChange(body: WatchHistoryStateChangeInput) {
        return await this.call<WatchHistoryStateChangeInput, WatchHistoryStateChangeOutput>(Operation.WatchHistoryStateChange, body, OperationType.WRITE);
    }

    async VideoWizard(body: VideoWizardInput) {
        return await this.call<VideoWizardInput, VideoWizardOutput>(Operation.VideoWizard, body, OperationType.READ);
    }

    async SubmitVideoWizard(body: SubmitVideoWizardInput) {
        return await this.call<SubmitVideoWizardInput, SubmitVideoWizardOutput>(Operation.SubmitVideoWizard, body, OperationType.WRITE);
    }

    async EnrichItemMetadata(body: EnrichItemMetadataInput) {
        return await this.call<EnrichItemMetadataInput, EnrichItemMetadataOutput>(Operation.EnrichItemMetadata, body, OperationType.READ);
    }

    async MetadataList(body: MetadataList.Request) {
        return await this.call<MetadataList.Request, MetadataList.Response>(Operation.MetadataList, body, OperationType.READ);
    }

    async Metadata(body: Metadata.Request) {
        return await this.call<Metadata.Request, Metadata.Response>(Operation.Metadata, body, OperationType.READ);
    }

    async GetPersonDetails(body: GetPersonDetailsInput) {
      return await this.call<GetPersonDetailsInput, Types.atv.wps.GetPersonDetailsOutput>(Operation.GetPersonDetails, body, OperationType.READ);
    }
    
    async Scout(body: ScoutInput) {
        return await this.call<ScoutInput, ScoutOutput>(Operation.Scout, body, OperationType.READ);
    }
}
